/*
 * Copyright (c) 2019
 *    Reliant Business Valuation, LLC - http://reliantvalue.com
 *    Author: Ronnel Bedana - ronnel@internalvaluation.com
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { Component, ViewChild, ElementRef, Input, forwardRef, Output, EventEmitter, AfterViewInit } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { CustomDocuments } from 'src/app/model/custom.documents';
import {CommaPipe} from "../../pipes/comma.pipe";

const noop = ():any => { };

@Component({
  selector: 'custom-document',
  templateUrl: 'custom-documents.component.html',
  styleUrls: ['custom-documents.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CustomDocumentComponent),
      multi: true
    }
  ],
})
export class CustomDocumentComponent implements ControlValueAccessor, AfterViewInit {
  @ViewChild('inputText') inputRef: ElementRef;

  @Input() document: CustomDocuments;
  @Input() editFlag = false;
  @Input() disabled = false;
  @Input() placeholder = 'Placeholder';
  @Output() valueChange: EventEmitter<CustomDocuments> = new EventEmitter();
  @Output() remove: EventEmitter<boolean> = new EventEmitter();

  private onChange: (_: any) => void = noop;
  private onTouched: () => void = noop;

  constructor(
    private currencyPipe: CommaPipe
  ) { }

  ngAfterViewInit(): void {
    if (this.editFlag) {
      // Gain Focus
      setTimeout(() => {
        if (this.inputRef) {
          // noinspection TypeScriptValidateJSTypes
          this.inputRef.nativeElement.focus();
        }
      }, 100);
    }

  }

  get value():any {
    return this.document;
  }

  set value(val) {
    this.document = val;
    this.onChange(val);
  }

  registerOnChange(fn): void {
    this.onChange = fn;
  }

  registerOnTouched(fn): void {
    this.onTouched = fn;
  }

  writeValue(value): void {
    this.document = value;
  }

  accept(): void {
    this.editFlag = false;
    this.placeholder = this.document.NAME;

    this.document.VALUE = true;

    // Propagate Changes to ngModel
    this.onChange(this.document);

    // Emit Back
    this.valueChange.emit(this.document);
  }

  clear(): void {

    // Propagate Changes to ngModel
    this.onChange(this.document);

    // Loose Focus
    if (this.inputRef.nativeElement) {
      // noinspection TypeScriptValidateJSTypes
      this.inputRef.nativeElement.blur();
    }
  }

  nameChanged(): void {
    // Propagate Changes to ngModel
    this.onChange(this.document);
  }

  onFocus(): void {
    this.onChange(this.document);
  }

  onBlur(): void {
    this.accept();
  }

  onPaste(event: any): void {
    let data;

    if ((<any>window).clipboardData && (<any>window).clipboardData.getData) // IE
      data = (<any>window).clipboardData.getData('Text');
    else data = (event.originalEvent || event).clipboardData.getData('text/plain');

    this.document.NAME = data;
    this.onChange(this.document);
    event.preventDefault();
  }

  onKeyPress(): void {
    this.onChange(this.document);
  }

  onLabelClick(): void {
    this.editFlag = true;

    if (!this.document.NAME) {
      this.document.NAME = this.placeholder;
      this.onChange(this.document);
    }

    // Gain Focus
    setTimeout(() => {
      if (this.inputRef) {
        // noinspection TypeScriptValidateJSTypes
        this.inputRef.nativeElement.focus();
      }
    }, 100);
  }

  onRemove(): void {
    this.remove.emit(true);
  }

  valueChanged(): void {
    this.accept();
  }
}
